import {useMemo} from "react";
import {useQueryClient} from "@tanstack/react-query";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {settingsUrl, signalsUrl} from "../services/categoryProfileUrls";
import type {ExternalSignals, SignalInput, StaffSignals} from "../types/signalsTypes";
import {done, failed} from "./notices";

export function useSignalsQuery(databagId: number, path: string) {
    const url = signalsUrl(databagId, path);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<ExternalSignals>(url)).data,
        enabled: databagId > 0 && Boolean(path),
        retry: false,
    });
}

/** Confirm or change the external taxonomy node, with Undo back to the previous one. */
export function useSignalsActions(databagId: number, path: string) {
    const qc = useQueryClient();
    return useMemo(() => {
        const put = async (node: string, code: string) => {
            const res = await queryHttp.put<ExternalSignals>(signalsUrl(databagId, path, true), {node, code});
            qc.setQueryData([signalsUrl(databagId, path)], res.data);
        };
        return {
            confirmMapping: async (node: string, code: string, previous: {node: string | null; code: string | null}) => {
                await put(node, code);
                done('Mapping confirmed.', () => put(previous.node ?? '', previous.code ?? ''));
            },
        };
    }, [qc, databagId, path]);
}

const staffUrl = (databagId: number) => settingsUrl(`signals/${databagId}`);

/** Staff only (settings page): the dataset's signals, added by hand while the provider feed is off. */
export function useStaffSignalsQuery(databagId: number, enabled: boolean) {
    return useCustomQuery({
        queryKey: [staffUrl(databagId)] as const,
        queryFn: async () => (await queryHttp.get<StaffSignals>(staffUrl(databagId))).data,
        enabled: enabled && databagId > 0,
        retry: false,
    });
}

export function useStaffSignalsActions(databagId: number) {
    const qc = useQueryClient();
    return useMemo(() => {
        const key = [staffUrl(databagId)];
        const write = async (method: 'post' | 'delete', url: string, data?: unknown) => {
            const res = await queryHttp.request<StaffSignals>({method, url, data});
            qc.setQueryData(key, res.data);
            // Every category view of this dataset may show the signal.
            void qc.invalidateQueries({predicate: q => String(q.queryKey[0]).includes(`category_profile/${databagId}/profile/`)});
            return res.data;
        };
        const item = (id: number) => `${staffUrl(databagId).replace(/\/$/, '')}/${id}/`;
        return {
            add: async (input: SignalInput) => {
                const res = await write('post', staffUrl(databagId), input);
                done('Signal added.', () => write('delete', item(res.id!)));
            },
            remove: async (id: number, title: string) => {
                try {
                    await write('delete', item(id));
                    done(`“${title}” removed.`, () => write('post', item(id)));
                } catch (e) {
                    failed(e);
                }
            },
        };
    }, [qc, databagId]);
}
